import React, { forwardRef } from 'react';

export interface SwitchProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /** The label text for the switch */
  label?: React.ReactNode;
  /** Optional description text below the label */
  description?: string;
  /** Size variant */
  size?: 'sm' | 'md';
}

/**
 * Toggle switch component for boolean settings
 * 
 * @example
 * ```tsx
 * <Switch
 *   id="active"
 *   label="Active"
 *   checked={isActive}
 *   onChange={(e) => setIsActive(e.target.checked)}
 * />
 * ```
 */
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  (
    {
      label,
      description,
      size = 'md',
      className = '',
      id,
      disabled,
      ...props
    },
    ref
  ) => {
    const descriptionId = description && id ? `${id}-description` : undefined;

    // Size variants
    const trackSize = size === 'sm' ? 'h-5 w-9' : 'h-6 w-11';
    const thumbSize = size === 'sm' ? 'size-4' : 'size-5';

    const switchElement = (
      <label
        htmlFor={id}
        className={`relative inline-flex cursor-pointer items-center ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
      >
        <input
          ref={ref}
          id={id}
          type="checkbox"
          role="switch"
          aria-describedby={descriptionId}
          disabled={disabled}
          className="peer sr-only"
          {...props}
        />
        <div
          className={`${trackSize} rounded-full bg-gray-200 peer-checked:bg-primary peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary dark:bg-white/10 dark:peer-checked:bg-primary dark:peer-focus-visible:outline-primary transition-colors duration-200 ${className}`}
        />
        <div
          className={`absolute left-0.5 top-0.5 ${thumbSize} rounded-full bg-white shadow-sm ring-1 ring-gray-900/5 transition-transform duration-200 ${size === 'sm' ? 'peer-checked:translate-x-4' : 'peer-checked:translate-x-5'}`}
        />
      </label>
    );

    if (!label) {
      return switchElement;
    }

    return (
      <div className="flex items-start gap-3">
        {switchElement}
        <div className="flex flex-col">
          <span
            className={`text-sm font-medium text-foreground ${disabled ? 'opacity-50' : ''}`}
          >
            {label}
          </span>
          {description && (
            <span
              id={descriptionId}
              className="text-sm text-gray-500 dark:text-gray-400"
            >
              {description}
            </span>
          )}
        </div>
      </div>
    );
  }
);

Switch.displayName = 'Switch';
